import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import {
  History,
  Group,
  EmojiEvents,
  Business,
  People,
  Settings,
  Computer,
  Devices,
  AttachMoney
} from '@mui/icons-material';
import theme from '../../theme/theme';

function AboutCompany() {
  return (
    <Box
      id="about"
      sx={{
        py: { xs: 6, md: 10 },
        background: "#f0f7ff",
        position: "relative",
      }}
    >
      <Container maxWidth="lg">
        {/* Headings */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.primary.main,
              fontSize: {
                xs: "clamp(1.3rem, 4vw, 1.5rem)",   
                sm: "clamp(1.8rem, 5vw, 2.4rem)",   
                md: "clamp(2.2rem, 6.5vw, 3rem)",   
              },

              fontWeight: 700,
            }}
          >
            About{" "}
            <Box component="span" sx={{ color: theme.palette.secondary.main }}>
              SAINI COLLECTION
            </Box>
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              color: theme.palette.primary.light,
              maxWidth: 800,
              mx: "auto",
              mt: 1,
              px: { xs: 2, md: 0 },
              fontSize: {
                xs: "clamp(0.75rem, 2vw, 1rem)",
                sm: "clamp(0.875rem, 2.3vw, 1.125rem)",
                md: "clamp(1rem, 2.5vw, 1.25rem)"
              }

            }}
          >
            New beginnings, big ideas — we craft websites & digital tools that
            help you grow online with ease
          </Typography>
        </Box>

        {/* Left & Right Cards */}
        <Grid container spacing={{ xs: 3, md: 6 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                background: "#ffffff",
                boxShadow: "0 15px 40px rgba(74, 123, 255, 0.15)",
                border: "1px solid rgba(74, 123, 255, 0.2)",
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: {
                      xs: "clamp(1.3rem, 3.5vw, 2rem)"
                    },

                    color: theme.palette.primary.main,
                    mb: 2,
                    fontWeight: 600,
                  }}
                >
                  Our Journey
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: {
                      xs: "clamp(0.75rem, 2vw, 1rem)"
                    },

                    mb: 3,
                    color: theme.palette.text.primary,
                    lineHeight: 1.8,
                  }}
                >
                  Saini Collection started with a simple idea — to make good
                  design & development accessible for everyone. From a small team
                  with big dreams, we're now on our way to help individuals &
                  businesses build their online presence with confidence.
                </Typography>

                <Box
                  sx={{
                    backgroundColor: "rgba(26, 58, 111, 0.05)",
                    borderRadius: 3,
                    p: 3,
                    mt: 3,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: {
                        xs: "clamp(1.3rem, 3.5vw, 2rem)"
                      },
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      mb: 1.5,
                    }}
                  >
                    Our Mission
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: {
                        xs: "clamp(0.75rem, 2vw, 1rem)",
                        sm: "clamp(0.875rem, 2.3vw, 1.125rem)",
                        md: "clamp(1rem, 2.5vw, 1.25rem)"
                      },
                      color: theme.palette.text.primary,
                      fontStyle: "italic",
                    }}
                  >
                    "Our mission is to deliver modern, clean, and reliable
                    digital solutions that help people grow. We believe in clear
                    communication, affordable service, and building trust with
                    every project we deliver."
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                background: "#ffffff",
                boxShadow: "0 15px 40px rgba(74, 123, 255, 0.15)",
                border: "1px solid rgba(74, 123, 255, 0.2)",
              }}
            >
              <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                <Grid container spacing={{ xs: 3, md: 4 }}>
                  {[
                    { icon: <History />, title: "Fresh Start", subtitle: "Innovative approach" },
                    { icon: <Group />, title: "Young Team", subtitle: "Passionate developers" },
                    { icon: <EmojiEvents />, title: "Quality First", subtitle: "Focused on results" },
                    { icon: <Business />, title: "Customer Care", subtitle: "We grow with you" },
                  ].map((item, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {React.cloneElement(item.icon, {
                          sx: {
                            fontSize: {
                              xs: "clamp(1.5rem, 4.4vw, 2.5rem)"
                            },

                            color: theme.palette.secondary.main,
                            mr: 2,
                          }
                        })}
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontSize: {
                                xs: "clamp(1.1rem, 3.1vw, 1.4rem)"
                              },
                              fontWeight: 600,
                              color: theme.palette.primary.main,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                              fontSize: {
                                xs: "clamp(0.75rem, 2.6vw, 1.1rem)"
                              },
                            }}
                          >
                            {item.subtitle}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 10, md: 10 }, textAlign: "center" }}>
          <Typography
            variant="h4"
            sx={{
              color: theme.palette.primary.main,
              fontSize: {
                xs: "clamp(1.5rem, 5vw, 3rem)"
              },
              fontWeight: 700,
            }}
          >
            Why Choose{" "}
            <Box component="span" sx={{ color: theme.palette.secondary.main }}>
              SAINI COLLECTION
            </Box>
          </Typography>

          <Box
            sx={{
              display: "flex",
              overflowX: "auto",
              gap: 3,
              py: 2,
              px: 1,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {[
              {
                title: "Creative Minds",
                description:
                  "A young team with fresh ideas & clean, modern designs.",
                icon: <People />,
              },
              {
                title: "Custom Solutions",
                description:
                  "We build websites & tools exactly how you need them",
                icon: <Settings />,
              },
              {
                title: "Friendly Support",
                description: "Clear communication & help whenever you need us",
                icon: <Computer />,
              },
              {
                title: "Modern Technology",
                description:
                  "We use the latest tech for smooth & secure websites.",
                icon: <Devices />,
              },
              {
                title: "Affordable Pricing",
                description:
                  "Quality work at reasonable prices, perfect for growing businesses.",
                icon: <AttachMoney />,
              },
            ].map((item, index) => (
              <Card
                key={index}
                sx={{
                  width: { xs: 340, sm: 300, md: 350 },
                  mt: { xs: 4, md: 10 },
                  minWidth: 260,
                  flexShrink: 0,
                  background: "#ffffff",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  border: "1px solid rgba(74, 123, 255, 0.1)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 15px 30px rgba(74, 123, 255, 0.2)",
                  },
                }}
              >
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 30,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                  </Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "2rem", md: "2.5rem" },
                      color: theme.palette.secondary.main,
                      height: 55,
                      width: 55,
                      justifyItems: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto",
                      mb: 1,
                      borderRadius: "50%",
                      backgroundColor: "rgba(74, 123, 255, 0.1)",
                    }}
                  >
                    {item.icon}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: { xs: "1.1rem", md: "1.4rem" },
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      mb: 1.5,
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.primary,
                      fontSize: { xs: "0.75rem", md: "1.1rem" },
                    }}
                  >
                    {item.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Button */}
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() =>
              document
                .getElementById("services")
                .scrollIntoView({ behavior: "smooth" })
            }
            sx={{
              mt: { xs: 6, md: 6 },
              px: { xs: 8, md: 6 },
              fontSize: {
                xs: "clamp(0.875rem, 2.5vw, 1rem)"
              },
              fontWeight: 700,
              borderWidth: 2,
            }}
          >
            Explore And Join Our Services
          </Button>
        </Box>
      </Container>
    </Box>
  );
}

export default AboutCompany;
